import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft, ShoppingBag, Package, CheckCircle, AlertTriangle,
  Calendar, Euro, Truck, User, FileText, Printer,
} from "lucide-react";
import type { CommandeFournisseur, EcheancePaiementFournisseur } from "@shared/schema";
import { useProductImageResolver } from "@/hooks/use-inventory";

const STATUTS: Record<string, { label: string; color: string }> = {
  "commandé":    { label: "Commandé",   color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" },
  "en_transit":  { label: "En transit", color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  "en_douane":   { label: "En douane",  color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  "reçu":        { label: "Reçu",       color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  "annulé":      { label: "Annulé",     color: "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400" },
};

const ECHEANCE_STATUTS: Record<string, { label: string; color: string }> = {
  "en_attente": { label: "En attente", color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" },
  "payé":       { label: "Payé",       color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300" },
  "en_retard":  { label: "En retard",  color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300" },
};

function formatDate(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function formatMontant(m: string | number | null | undefined) {
  if (m === null || m === undefined || m === "") return "—";
  const n = Number(m);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n);
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <span className="text-sm text-muted-foreground min-w-0 shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{value || "—"}</span>
    </div>
  );
}

function generatePDF(commande: CommandeFournisseur & { echeancesDetail?: EcheancePaiementFournisseur[] }) {
  const lignes = (commande.lignes as any[]) || [];
  const totalHT = Number(commande.montantTotal) || 0;
  const remise = Number(commande.remise) || 0;
  const fraisLog = Number(commande.fraisLogistique) || 0;
  const netAPayer = totalHT - remise + fraisLog;

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8"/>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #222; margin: 0; padding: 30px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; }
  .logo { font-size: 24px; font-weight: bold; color: #1a1a1a; }
  .logo span { color: #e85d1c; }
  .company-info { text-align: right; font-size: 11px; color: #555; }
  h2 { font-size: 18px; border-bottom: 2px solid #e85d1c; padding-bottom: 8px; margin-top: 20px; }
  .blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
  .block { background: #f9f9f9; padding: 12px; border-radius: 6px; }
  .block h3 { font-size: 11px; color: #999; text-transform: uppercase; margin: 0 0 8px; }
  .block p { margin: 3px 0; font-size: 12px; }
  table { width: 100%; border-collapse: collapse; margin: 20px 0; }
  th { background: #333; color: white; padding: 8px; text-align: left; font-size: 11px; }
  td { padding: 8px; border-bottom: 1px solid #eee; font-size: 11px; }
  tr:nth-child(even) td { background: #f9f9f9; }
  .totals { margin-top: 10px; text-align: right; }
  .totals td { border: none; }
  .total-line { font-size: 14px; font-weight: bold; color: #e85d1c; }
  .footer { margin-top: 30px; font-size: 10px; color: #888; border-top: 1px solid #eee; padding-top: 12px; }
</style>
</head>
<body>
<div class="header">
  <div>
    <div class="logo">JOBO<span>TECH</span></div>
    <div style="font-size:11px;color:#555;margin-top:5px;">
      SAS Jobotech — 18 rue de la Biche, 78160 Marly-le-Roi<br/>
      contact@jobotech.fr — www.jobotech.fr
    </div>
  </div>
  <div class="company-info">
    <strong>BON DE COMMANDE</strong><br/>
    ${commande.numeroPo || "N/A"}<br/>
    Date : ${formatDate(commande.dateCommande)}<br/>
    ${commande.numeroPi ? "Réf PI : " + commande.numeroPi : ""}
  </div>
</div>

<div class="blocks">
  <div class="block">
    <h3>Fournisseur</h3>
    <p><strong>${commande.fournisseur || "Pudu Robotics HK Limited"}</strong></p>
    ${commande.contactFournisseur ? `<p>${commande.contactFournisseur}</p>` : ""}
  </div>
  <div class="block">
    <h3>Livraison</h3>
    <p><strong>SAS Jobotech</strong></p>
    <p>18 rue de la Biche</p>
    <p>78160 Marly-le-Roi, France</p>
    <p>Date prévue : ${formatDate(commande.dateLivraisonPrevue)}</p>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th>#</th>
      <th>Article & Description</th>
      <th style="text-align:center">Qté</th>
      <th style="text-align:right">Prix unitaire</th>
      <th style="text-align:right">Montant</th>
    </tr>
  </thead>
  <tbody>
    ${lignes.map((l: any, i: number) => `
      <tr>
        <td>${i + 1}</td>
        <td>${l.nom || l.sku || "—"}${l.sku ? `<br/><span style="color:#999;font-size:10px">${l.sku}</span>` : ""}</td>
        <td style="text-align:center">${l.quantite || 1}</td>
        <td style="text-align:right">${formatMontant(l.prixUnitaire)}</td>
        <td style="text-align:right">${formatMontant(l.prixTotal)}</td>
      </tr>
    `).join("")}
  </tbody>
</table>

<table class="totals">
  <tr><td>Sous-total HT</td><td>${formatMontant(totalHT)}</td></tr>
  ${remise > 0 ? `<tr><td>Remise</td><td>- ${formatMontant(remise)}</td></tr>` : ""}
  ${fraisLog > 0 ? `<tr><td>Frais logistique</td><td>${formatMontant(fraisLog)}</td></tr>` : ""}
  <tr class="total-line"><td><strong>TOTAL</strong></td><td><strong>${formatMontant(netAPayer)}</strong></td></tr>
</table>

${commande.modalitesPaiement ? `
<div style="margin-top:20px;background:#f9f9f9;padding:12px;border-radius:6px;">
  <strong>Modalités de paiement :</strong> ${commande.modalitesPaiement}
</div>` : ""}

<div class="footer">
  Document généré le ${new Date().toLocaleDateString("fr-FR")} — Jobotech SAS
</div>
</body>
</html>`;

  const win = window.open("", "_blank");
  if (win) {
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 500);
  }
}

export default function AchatsDetail() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const resolveImage = useProductImageResolver();

  const { data: commande, isLoading } = useQuery<CommandeFournisseur & { echeancesDetail: EcheancePaiementFournisseur[] }>({
    queryKey: ["/api/achats", id],
    queryFn: () => authFetch(`/api/achats/${id}`).then(r => r.json()),
    enabled: !!id,
  });

  const marquerRecuMutation = useMutation({
    mutationFn: () => authFetch(`/api/achats/${id}/marquer-recu`, { method: "POST" }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/achats", id] });
      queryClient.invalidateQueries({ queryKey: ["/api/achats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/achats/stats"] });
      toast({ title: "Commande marquée comme reçue" });
    },
  });

  const payerEcheanceMutation = useMutation({
    mutationFn: (echeanceId: string) =>
      authFetch(`/api/achats/echeances/${echeanceId}/marquer-paye`, { method: "PUT" }).then(r => r.json()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/achats", id] });
      toast({ title: "Échéance marquée comme payée" });
    },
  });

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (!commande) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>Commande introuvable.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const lignes = (commande.lignes as any[]) || [];
  const echeances = commande.echeancesDetail || [];
  const today = new Date().toISOString().split("T")[0];

  const totalHT = Number(commande.montantTotal) || 0;
  const remise = Number(commande.remise) || 0;
  const fraisLog = Number(commande.fraisLogistique) || 0;
  const netAPayer = totalHT - remise + fraisLog;

  const totalEcheances = echeances.reduce((s, e) => s + Number(e.montant || 0), 0);
  const totalPaye = echeances.filter(e => e.statut === "payé").reduce((s, e) => s + Number(e.montant || 0), 0);
  const pctPaye = totalEcheances > 0 ? Math.round((totalPaye / totalEcheances) * 100) : 0;
  const hasRetard = echeances.some(e => e.statut !== "payé" && e.dateEcheance && e.dateEcheance < today);

  const statutInfo = STATUTS[commande.statut || "commandé"] || { label: commande.statut || "—", color: "bg-gray-100 text-gray-600" };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/achats")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <ShoppingBag className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-xl font-bold">{commande.numeroPo || "Commande fournisseur"}</h1>
            {commande.numeroPi && (
              <p className="text-sm text-muted-foreground">PI : {commande.numeroPi}</p>
            )}
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statutInfo.color}`}>
            {statutInfo.label}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => generatePDF(commande)} data-testid="button-generer-pdf">
            <Printer className="w-4 h-4 mr-2" />
            Bon de commande PDF
          </Button>
          {commande.statut !== "reçu" && commande.statut !== "annulé" && (
            <Button onClick={() => marquerRecuMutation.mutate()} disabled={marquerRecuMutation.isPending} data-testid="button-marquer-recu">
              <CheckCircle className="w-4 h-4 mr-2" />
              Marquer comme reçu
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Colonne gauche — infos générales */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <FileText className="w-4 h-4" /> Informations générales
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <InfoRow label="N° PO" value={commande.numeroPo} />
              <InfoRow label="N° PI" value={commande.numeroPi} />
              <InfoRow label="Fournisseur" value={commande.fournisseur} />
              <InfoRow label="Contact" value={commande.contactFournisseur} />
              <InfoRow label="Date commande" value={formatDate(commande.dateCommande)} />
              <InfoRow label="Livraison prévue" value={formatDate(commande.dateLivraisonPrevue)} />
              {commande.dateReception && (
                <InfoRow label="Date réception" value={formatDate(commande.dateReception)} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Euro className="w-4 h-4" /> Montants
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-0">
              <InfoRow label="Montant total HT" value={formatMontant(commande.montantTotal)} />
              {remise > 0 && <InfoRow label="Remise" value={`- ${formatMontant(remise)}`} />}
              {fraisLog > 0 && <InfoRow label="Frais logistique" value={formatMontant(fraisLog)} />}
              <InfoRow
                label="Net à payer"
                value={<span className="font-bold text-primary">{formatMontant(netAPayer)}</span>}
              />
              {commande.modalitesPaiement && (
                <div className="pt-3 mt-2 border-t border-border/50">
                  <p className="text-xs text-muted-foreground mb-1">Modalités de paiement</p>
                  <p className="text-sm">{commande.modalitesPaiement}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Colonne droite — lignes produits */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Package className="w-4 h-4" /> Lignes produits ({lignes.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-border">
              {lignes.length === 0 ? (
                <div className="px-4 py-8 text-center text-muted-foreground text-sm">
                  Aucune ligne produit
                </div>
              ) : (
                lignes.map((l: any, i: number) => {
                  const imgSrc = resolveImage(l.sku, l.imageUrl);
                  return (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    {imgSrc ? (
                      <img src={imgSrc} alt={l.nom} className="w-10 h-10 rounded object-cover border border-border shrink-0" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-muted flex items-center justify-center shrink-0">
                        <Package className="w-4 h-4 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{l.nom || l.sku}</p>
                      {l.sku && <p className="text-xs text-muted-foreground">{l.sku}</p>}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm">×{l.quantite}</p>
                      <p className="text-xs text-muted-foreground">{formatMontant(l.prixUnitaire)} / u.</p>
                    </div>
                    <div className="text-right shrink-0 font-medium text-sm w-24">
                      {formatMontant(l.prixTotal)}
                    </div>
                  </div>
                  );
                })
              )}
            </div>
            {lignes.length > 0 && (
              <div className="px-4 py-3 border-t border-border bg-muted/30 space-y-1">
                {remise > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Remise</span><span>- {formatMontant(remise)}</span>
                  </div>
                )}
                {fraisLog > 0 && (
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Frais logistique</span><span>{formatMontant(fraisLog)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-sm">
                  <span>Total</span><span>{formatMontant(netAPayer)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Bloc Échéances de paiement */}
      {echeances.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Échéances de paiement
              </CardTitle>
              <div className="flex items-center gap-3">
                <Progress value={pctPaye} className="w-24 h-2" />
                <span className="text-sm text-muted-foreground">{pctPaye}% payé</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {hasRetard && (
              <div className="mx-4 mb-3 p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">
                  Une ou plusieurs échéances sont en retard de paiement.
                </p>
              </div>
            )}
            <div className="divide-y divide-border">
              {echeances.map(e => {
                const isLate = e.statut !== "payé" && e.dateEcheance && e.dateEcheance < today;
                const displayStatut = isLate ? "en_retard" : (e.statut || "en_attente");
                const statInfo = ECHEANCE_STATUTS[displayStatut] || { label: displayStatut, color: "bg-gray-100" };
                return (
                  <div key={e.id} className="flex items-center gap-4 px-4 py-3">
                    <div className="flex-1">
                      <p className="text-sm font-medium">{e.label || `Échéance`}</p>
                      <div className="flex items-center gap-3 mt-0.5">
                        {e.pourcentage && (
                          <span className="text-xs text-muted-foreground">{e.pourcentage}%</span>
                        )}
                        {e.dateEcheance && (
                          <span className="text-xs text-muted-foreground">
                            Échéance : {formatDate(e.dateEcheance)}
                          </span>
                        )}
                        {e.datePaiement && (
                          <span className="text-xs text-green-600">
                            Payé le {formatDate(e.datePaiement)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-sm">{formatMontant(e.montant)}</p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statInfo.color}`}>
                      {statInfo.label}
                    </span>
                    {e.statut !== "payé" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => payerEcheanceMutation.mutate(e.id)}
                        disabled={payerEcheanceMutation.isPending}
                        data-testid={`button-payer-echeance-${e.id}`}
                      >
                        Marquer payé
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bloc réception */}
      {commande.statut === "reçu" && (
        <Card className="border-green-200 dark:border-green-800 bg-green-50/50 dark:bg-green-950/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm font-medium text-green-800 dark:text-green-300">
                  Commande reçue le {formatDate(commande.dateReception)}
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                  Pensez à créer les fiches robots dans le référentiel pour les unités commandées.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="ml-auto border-green-300 text-green-700"
                onClick={() => window.location.href = "/robots"}
              >
                Aller aux robots
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
